import React from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import Immutable from 'immutable';
import {decamelize} from 'humps';
import queryString from 'query-string';
import {castValue} from 'utils';
import Video from './video';

// https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/btoa#Unicode_strings
export const utoa = str => window.btoa(unescape(encodeURIComponent(str)));
export const atou = str => decodeURIComponent(escape(window.atob(unescape(str))));

// Configurations

const getUpdatedConfigs = (config, current) => (
    Immutable.fromJS(current)
        .filter((currentValue, key) => !['', undefined, currentValue].includes(config.get(key)))
        .mapEntries(([key]) => [key, config.get(key)])
);

export const parseConfigUpdates = (config, current) => {
    const ignore = ['mdpsVersion', 'jobId', 'shortCode', 'videoId', 'videoProvider', 'panoptoHost'];
    return getUpdatedConfigs(config, current).filter((_, key) => !ignore.includes(key));
};

// Options
export const validateQuerystringConfig = config => {
    const mdpsVersion = Number(config.mdpsVersion);
    if (mdpsVersion) {
        if (![1, 3].includes(mdpsVersion)) return false;
        if (mdpsVersion === 1 && !config.shortCode) return false;
        if (mdpsVersion === 3 && !config.jobId) return false;
    }

    if (['kaltura', 'brightcove'].includes(config.videoProvider) && !config.accountId) return false;

    if (['kaltura', 'jwplayer', 'brightcove'].includes(config.videoProvider) && !config.playerId) return false;

    return true;
};

const SHARE_LINK_QS_KEY = 'share';

export const parseQuerystringConfig = (parsed, defaults) => {
    let config = {};
    if (parsed[SHARE_LINK_QS_KEY]) {
        config = JSON.parse(atou(parsed[SHARE_LINK_QS_KEY]));
        config.readOnly = true;
    } else {
        // Options
        if (parsed.mdpsVersion) config.mdpsVersion = parsed.mdpsVersion;
        if (parsed.jobId) config.jobId = parsed.jobId;
        if (parsed.shortCode) config.shortCode = parsed.shortCode;
        if (parsed.manifestId) config.shortCode = parsed.manifestId; // Note: Legacy
        if (parsed.disableEmbed) config.disableEmbed = castValue(parsed.disableEmbed);
        if (parsed.disableLink) config.disableLink = castValue(parsed.disableLink);
        // Video
        if (parsed.videoId) config.videoId = parsed.videoId;
        if (parsed.videoProvider) config.videoProvider = parsed.videoProvider;
        if (parsed.panoptoHost) config.panoptoHost = parsed.panoptoHost;
        // OVPs
        if (parsed.playerId) config.playerId = parsed.playerId;
        if (parsed.accountId) config.accountId = parsed.accountId;
        // Metadata
        if (parsed.videoMetadata) config.videoMetadata = JSON.parse(decodeURIComponent(parsed.videoMetadata));
        // Files
        if (parsed.videoProvider === 'videojs') {
            config.videoFormats = {};
            if (parsed.mp4) config.videoFormats.mp4 = parsed.mp4;
            if (parsed.webm) config.videoFormats.webm = parsed.webm;
        }
    }

    if (Object.keys(config).length === 0) return [null, null];
    if (!validateQuerystringConfig(config, defaults)) return [{}, true];
    return [config, false];
};

export const getConfigFromQueryString = (querystring, defaults) => {
    try {
        const parsed = queryString.parse(querystring);
        const config = parseQuerystringConfig(parsed, defaults);
        if (config) return config;
        return [null, null];
    } catch (error) {
        console.error(error); // eslint-disable-line no-console
        return [null, true];
    }
};

export const getDataAttrs = (config, defaults, string = false) => {
    const attrs = getUpdatedConfigs(config, defaults)
        .mapEntries(([key, value]) => [`data-${decamelize(key, {separator: '-'})}`, value]);

    if (!string) return attrs;
    return attrs.map((value, key) => `${key}="${value}"`).toList().join(' ');
};

export const getShortUrl = async (longUrl) => {
    const response = await fetch(`${VWRAP.urlShortener}/main/generate/?url_base=${longUrl}`);
    const data = await response.json();
    return data.short_url;
};

export const getShareLink = async (config) => {
    const encoded = utoa(JSON.stringify(config) || '');
    return await getShortUrl(`${VWRAP.cdn}?${SHARE_LINK_QS_KEY}=${encoded}`);
};

export const getEmbedCode = (config, includeScript) => {
    const videoMarkup = renderToStaticMarkup(
        <Video
            id={config.get('videoId')}
            width={config.get('videoSize')}
            height={config.get('videoSize') * 0.5625}
            provider={config.get('videoProvider')}
            panopto_host={config.get('panoptoHost')}
            playerId={config.get('playerId')}
            accountId={config.get('accountId')}
            urls={config.get('urls')}
            options={config}
            export={true}
        />,
    );
    const embedScript = `<script src="${VWRAP.cdn}vwrap.js"></script>`;

    const embedParts = [];
    embedParts.push(videoMarkup);

    if (includeScript) embedParts.push(embedScript);

    return embedParts.join('\n');
};

export const getJsonLD = (videoData = {}, includeTranscript) => {
    const {transcript, ...rest} = videoData;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        ...rest,
    };

    if (includeTranscript) schema.transcript = transcript;

    return `<script type="application/ld+json">\n${JSON.stringify(schema, null, 4)}\n</script>`;
};



// WEBPACK FOOTER //
// ./scripts/test-page/utils.js